import { EnvelopeIcon, PhoneIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { Link } from "react-router-dom";
import { YStack } from "tamagui";
import { useLoginAuth } from "../../hooks/LoginAuthContext";
import { SubmitButton } from "../shared/buttons/submit-button";
import Card from "../shared/card";
import { GoogleLogo } from "../shared/icons/GoogleLogo";
import PhoneInput from "../shared/phone-input";
import TamaguiInput from "../shared/tamagui-input";
import Typography from "../shared/typography";

interface LoginFormProps {
  onToggleView: () => void;
}

function LoginForm({ onToggleView }: LoginFormProps) {
  const { login, signInWithGoogle, isLoading, loginError } = useLoginAuth();
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [useEmail, setUseEmail] = useState(true);
  const [password, setPassword] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await login(useEmail ? { email, password } : { phone, password });
  };

  const handleGoogleSignIn = async () => {
    await signInWithGoogle();
  };

  return (
    <Card
      className="w-full max-w-md mx-auto"
      variant="cream"
      elevation={2}
      hoverEffect
    >
      <Card.Header className="text-center" variant="cream">
        <Typography variant="h3" className="text-primary">
          Log In
        </Typography>
      </Card.Header>

      <Card.Body>
        {loginError && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-md">
            <Typography variant="body2">{loginError}</Typography>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <YStack gap="$3">
            <div className="flex flex-row gap-2 items-end">
              <div className="flex-1">
                {useEmail ? (
                  <TamaguiInput
                    id="email"
                    label="Email"
                    value={email}
                    onChangeText={setEmail}
                    placeholder="Enter your email"
                    fullWidth
                    inputType="email"
                  />
                ) : (
                  <PhoneInput
                    id="phone"
                    label="Phone"
                    value={phone}
                    onChangeText={setPhone}
                    placeholder="(123) 456-7890"
                    fullWidth
                  />
                )}
              </div>

              <div className="flex-none mb-2">
                <SubmitButton
                  type="button"
                  onClick={() => setUseEmail(!useEmail)}
                  className="w-6 h-6"
                  iconOnly={true}
                  size="sm"
                  icon={
                    useEmail ? (
                      <PhoneIcon className="w-4 h-4" color="white" />
                    ) : (
                      <EnvelopeIcon className="w-4 h-4" color="white" />
                    )
                  }
                />
              </div>
            </div>
            {useEmail && (
              <TamaguiInput
                id="password"
                label="Password"
                value={password}
                onChangeText={setPassword}
                placeholder="Enter your password"
                fullWidth
                inputType="password"
              />
            )}

            <div className="text-right">
              <Link
                to="/forgot-password"
                className="text-sm text-primary hover:underline"
              >
                Forgot password?
              </Link>
            </div>

            <div className="pt-2">
              <SubmitButton
                onSubmit={handleSubmit}
                isDisabled={
                  (!useEmail && !phone) || (useEmail && !password) || isLoading
                }
                iconOnly={false}
                size="md"
                className="w-full"
              >
                Log In
              </SubmitButton>
            </div>
          </YStack>
        </form>

        <div className="mt-4 relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-primary"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <Typography
              variant="caption"
              className="px-2 bg-teal-50 text-primary"
            >
              Or continue with
            </Typography>
          </div>
        </div>

        <div className="mt-4">
          <button
            type="button"
            onClick={handleGoogleSignIn}
            className="w-full flex justify-center items-center py-2 px-4 border border-primary rounded-md shadow-sm bg-white text-sm font-medium text-primary hover:bg-primary hover:text-white transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <GoogleLogo />
            Sign in with Google
          </button>
        </div>
      </Card.Body>

      <Card.Footer divider className="text-center" variant="default">
        <Typography variant="body2" className="text-terracotta">
          Don't have an account?{" "}
          <button
            onClick={onToggleView}
            className="text-primary hover:text-primary-dark font-medium"
          >
            Sign Up
          </button>
        </Typography>
      </Card.Footer>
    </Card>
  );
}

export default LoginForm;
